import { API } from '#/config/api';
import { USER_TOKEN, USER_INFO } from '#/config/storage';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import axios from './axios';
import { NotifyData } from '#/services/NotificationService';
import { PagenateData } from '#/components/parts/Pagenate';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Tokyo');

export interface UserDeviceCount {
  owner: number;
  child: number;
}

export interface UserItem {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  device_count: UserDeviceCount;
  notify: NotifyData;
  created_at: Date;
  updated_at: Date;
  payment_plan: string;
  admin_id: string;
  token: string;
}

export interface UserData {
  page: PagenateData;
  data: UserItem[];
}

export interface UserToken {
  access_token: string;
  expires: string;
  refresh_token: string;
  access_token_type: string;
  id_token: string;
}

/**
 * ユーザデータクラス
 *
 */
class UserService {
  private loginedUser: null | undefined | UserItem;

  constructor(loginedUser?: null | undefined | UserItem) {
    this.loginedUser = loginedUser;
  }

  /**
   * ユーザ一覧を検索する
   *
   */
  public async getList(query: { [key: string]: any }): Promise<UserData> {
    const res = await axios.get(API.UsersSearchEndpoint, {
      params: query,
      headers: {
        token: this.loginedUser!.token,
      },
    });

    return {
      ...res.data,
      page: {
        ...res.data.page,
        size: query?.size,
      },
    };
  }

  /**
   * ユーザ情報の取得
   *
   */
  public async getUser(): Promise<UserItem | null> {
    // Storageからのデータ取得がうまくいかないので、リトライする
    let user_token;
    for (let i = 0; i < 3; i++) {
      user_token = localStorage.getItem(USER_TOKEN);
      if (user_token) {
        break;
      }

      // 1秒間待機
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    if (!user_token) {
      return null;
    }

    const token = JSON.parse(user_token);
    const refresh = !this.isAvailable(token);

    return this.syncUser(token, refresh);
  }

  /**
   * ユーザを新規登録する
   *
   */
  public async signUp(email: string, role?: string) {
    const res = await axios.post(
      API.UsersEndpoint,
      {
        email,
        role,
      },
      {
        headers: {
          token: this.loginedUser ? this.loginedUser!.token : '',
        },
      }
    );

    return res.data;
  }

  /**
   * メールアドレス検証
   *
   */
  public async verify(
    email: string,
    password: string,
    code: string,
    name: string,
    owner_device_id?: string,
    child_device_id?: string
  ) {
    const res = await axios.post(API.UsersVerifyEndpoint, {
      email,
      password,
      code,
      name,
      owner_device_id,
      child_device_id,
    });

    if (res.data) {
      const expires = dayjs()
        .tz()
        .add(res.data.expires, 'second')
        .toISOString();
      localStorage.setItem(
        USER_TOKEN,
        JSON.stringify({ ...res.data, expires: expires })
      );
      await null;
    }
  }

  /**
   * ログイン処理
   *
   */
  public async signIn(email: string, password: string) {
    const res = await axios.put(API.UsersEndpoint, {
      email,
      password,
    });

    if (res.data) {
      const { token } = res.data;
      const expires = dayjs()
        .tz()
        .add(Number(token.expires), 'second')
        .toISOString();
      localStorage.setItem(
        USER_TOKEN,
        JSON.stringify({ ...token, expires: expires })
      );
      await null;
    }
  }

  /**
   * ログアウト処理
   *
   */
  public async signOut() {
    await axios.delete(API.UsersEndpoint, {
      headers: {
        token: this.loginedUser!.token,
      },
    });

    localStorage.removeItem(USER_TOKEN);
    await null;
  }

  /**
   * ユーザ情報が正しいか確認
   *
   */
  private isAvailable(token: UserToken): boolean {
    if (!token.expires) {
      return false;
    }

    const expire = dayjs(token.expires!).tz();
    if (expire.toISOString() !== token.expires!) {
      return false;
    }

    return dayjs().tz().isBefore(expire, 'minute');
  }

  /**
   * ローカルストレージにユーザ情報を設定
   *
   */
  private async syncUser(token: UserToken, refresh: boolean) {
    try {
      let userInfo = null;
      const info = localStorage.getItem(USER_INFO);
      if( info ) {
        userInfo = JSON.parse(info);
      }

      if (refresh) {
        const res = await axios.put(API.UsersTokenEndpoint, {
          refresh_token: token.refresh_token,
        });
        if (!res.data) {
          localStorage.removeItem(USER_TOKEN);
          return null;
        }

        token = {
          ...token,
          ...res.data,
        };

        const expires = dayjs()
          .tz()
          .add(Number(token.expires), 'second')
          .toISOString();
        localStorage.setItem(
          USER_TOKEN,
          JSON.stringify({
            ...token,
            expires,
          })
        );

        userInfo = null;
      }

      if (!userInfo) {
        // ユーザデータのみ取得
        const user = await axios.get(API.UsersEndpoint, {
          headers: {
            token: token.id_token,
          },
        });

        userInfo = user.data;
        localStorage.setItem(USER_INFO, JSON.stringify(userInfo));
      }

      return {
        ...userInfo,
        token: token.id_token,
      };
    } catch (e) {
      return null;
    }
  }
}

export default UserService;
